import React, { useState, useRef, useEffect } from "react";
import AiResult from "./AiResult";
import katex from "katex";
import { preprocessMath } from "../utils/mathPreprocess";
import { getAuthHeaders } from "../utils/auth";
import { useLanguage } from "../utils/i18n";

function escHtml(s) {
  return (s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function renderKatex(latex, displayMode) {
  try {
    return katex.renderToString(latex, { displayMode, throwOnError: false, output: "html" });
  } catch {
    return `<code>${escHtml(latex)}</code>`;
  }
}

/** Convert an AI markdown string (with $$/$$ math) to Word-friendly HTML. */
function mdToHtml(rawText) {
  if (!rawText) return "";
  const text = preprocessMath(rawText);

  // First pass: extract display-math blocks $$ ... $$ (possibly multi-line)
  const segments = [];
  const dRe = /\$\$([\s\S]*?)\$\$/g;
  let last = 0, m;
  while ((m = dRe.exec(text)) !== null) {
    if (m.index > last) segments.push({ type: "text", c: text.slice(last, m.index) });
    segments.push({ type: "dmath", c: m[1].trim() });
    last = dRe.lastIndex;
  }
  if (last < text.length) segments.push({ type: "text", c: text.slice(last) });

  return segments
    .map((seg) => {
      if (seg.type === "dmath") {
        return `<div style="text-align:center;margin:18px 0;padding:6px 0">${renderKatex(seg.c, true)}</div>`;
      }
      return textSegToHtml(seg.c);
    })
    .join("");
}

function textSegToHtml(text) {
  // Inline math $...$
  text = text.replace(/\$([^$\n]+?)\$/g, (_, math) => renderKatex(math, false));
  // Bold / italic
  text = text.replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>");
  text = text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  text = text.replace(/\*([^*\n]+?)\*/g, "<em>$1</em>");

  const lines = text.split("\n");
  const out = [];
  let inUl = false;

  for (const raw of lines) {
    const l = raw;
    if (/^### /.test(l)) {
      if (inUl) { out.push("</ul>"); inUl = false; }
      out.push(`<h3 style="color:#3a56d4;font-size:14px;font-weight:700;margin:16px 0 5px">${l.slice(4)}</h3>`);
    } else if (/^## /.test(l)) {
      if (inUl) { out.push("</ul>"); inUl = false; }
      out.push(`<h2 style="color:#4f6ef7;font-size:16px;font-weight:700;margin:22px 0 7px">${l.slice(3)}</h2>`);
    } else if (/^# /.test(l)) {
      if (inUl) { out.push("</ul>"); inUl = false; }
      out.push(`<h2 style="color:#4f6ef7;font-size:18px;font-weight:700;margin:22px 0 7px">${l.slice(2)}</h2>`);
    } else if (/^[-*] /.test(l)) {
      if (!inUl) { out.push('<ul style="margin:6px 0 6px 22px;padding:0">'); inUl = true; }
      out.push(`<li style="margin:4px 0;line-height:1.8;font-size:13px">${l.slice(2)}</li>`);
    } else if (l.trim() === "") {
      if (inUl) { out.push("</ul>"); inUl = false; }
    } else {
      if (inUl) { out.push("</ul>"); inUl = false; }
      out.push(`<p style="margin:5px 0;line-height:1.8;font-size:13px">${l}</p>`);
    }
  }
  if (inUl) out.push("</ul>");
  return out.join("");
}

function downloadAsWord(notes) {
  const html = `<!DOCTYPE html>
<html xmlns:o='urn:schemas-microsoft-com:office:office'
      xmlns:w='urn:schemas-microsoft-com:office:word'
      xmlns='http://www.w3.org/TR/REC-html40'>
<head>
  <meta charset='UTF-8'>
  <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16/dist/katex.min.css">
  <style>
    body { font-family: Calibri, Arial, sans-serif; margin: 60px; color: #1e293b; font-size: 13px; line-height: 1.7; }
    h1 { color: #4f6ef7; font-size: 24px; border-bottom: 2px solid #4f6ef7; padding-bottom: 8px; margin-bottom: 6px; }
    .meta { color: #94a3b8; font-size: 11px; margin-bottom: 28px; }
    .note-title { color: #3a56d4; font-size: 17px; font-weight: 700; margin: 0 0 3px; }
    .note-ts { color: #94a3b8; font-size: 11px; margin-bottom: 12px; }
    hr { border: none; border-top: 1px solid #e2e8f0; margin: 28px 0; }
    .katex { font-size: 1.05em; }
    .katex-display { overflow-x: auto; }
  </style>
</head>
<body>
  <h1>📚 My Learning Notes</h1>
  <p class="meta">Generated on ${new Date().toLocaleString()}</p>
  ${notes
    .map(
      (n, i) => `
  <div style="margin-bottom:24px">
    <p class="note-title">${i + 1}. ${escHtml(n.title)}</p>
    <p class="note-ts">${n.timestamp}</p>
    ${mdToHtml(n.content)}
  </div>
  <hr>`
    )
    .join("")}
</body>
</html>`;

  const blob = new Blob([html], { type: "application/msword" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "learning-notes.doc";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function NotesTab({ notes, onAddNote, onDeleteNote, onRenameNote, onUpdateNote, subfolders, onAddSubfolder, onDeleteSubfolder, onMoveNote, onMoveFolderToParent, folderOrder, onReorderFolderOrder }) {
  const { t, lang } = useLanguage();
  const [folderSortOrders, setFolderSortOrders] = useState({});
  const getFolderSort = (folderId) => folderSortOrders[String(folderId)] || "newest";
  const setFolderSort = (folderId, order) => setFolderSortOrders((prev) => ({ ...prev, [String(folderId)]: order }));
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [titleDraft, setTitleDraft] = useState("");
  const [editingContentId, setEditingContentId] = useState(null);
  const [contentDraft, setContentDraft] = useState("");
  const [dragNoteId, setDragNoteId] = useState(null);
  const [dragFolderId, setDragFolderId] = useState(null);
  const [dragOverNoteTarget, setDragOverNoteTarget] = useState(undefined);
  const [dragOverFolderTarget, setDragOverFolderTarget] = useState(undefined);
  const [folderDropZone, setFolderDropZone] = useState(null); // { targetId, pos } for reorder
  const [addingFolder, setAddingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [collapsedFolders, setCollapsedFolders] = useState(() => {
    try {
      const s = localStorage.getItem("notes-collapsed-folders");
      return s ? new Set(JSON.parse(s)) : new Set();
    } catch { return new Set(); }
  });
  const titleInputRef = useRef(null);
  const contentInputRef = useRef(null);
  const newFolderRef = useRef(null);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [savedSel, setSavedSel] = useState({ start: 0, end: 0 });
  const [confirmPending, setConfirmPending] = useState(null); // { type: "note"|"folder", id, name }

  const askDeleteNote = (note) => setConfirmPending({ type: "note", id: note.id, name: note.title });
  const askDeleteFolder = (folder) => setConfirmPending({ type: "folder", id: folder.id || folder, name: (subfolders || []).find(f => String(f.id) === String(folder.id || folder))?.name || "" });
  const confirmDelete = () => {
    if (!confirmPending) return;
    if (confirmPending.type === "note") onDeleteNote(confirmPending.id);
    else onDeleteSubfolder(confirmPending.id);
    setConfirmPending(null);
  };

  useEffect(() => {
    if (editingTitleId && titleInputRef.current) titleInputRef.current.focus();
  }, [editingTitleId]);

  useEffect(() => {
    if (editingContentId && contentInputRef.current) {
      contentInputRef.current.focus();
      contentInputRef.current.style.height = "auto";
      contentInputRef.current.style.height = contentInputRef.current.scrollHeight + "px";
    }
  }, [editingContentId]);

  useEffect(() => {
    if (addingFolder && newFolderRef.current) newFolderRef.current.focus();
  }, [addingFolder]);

  const sortNotesFor = (notesList, folderId) => {
    const order = getFolderSort(folderId);
    return [...notesList].sort((a, b) => {
      const ta = a.sortTs || a.id;
      const tb = b.sortTs || b.id;
      return order === "newest" ? tb - ta : ta - tb;
    });
  };

  const rootNotes = sortNotesFor(notes.filter((n) => !n.folder), "general");
  const notesByFolder = Object.fromEntries(
    (subfolders || []).map((f) => [f.id, sortNotesFor(notes.filter((n) => n.folder === f.id), f.id)])
  );
  const topLevelFolders = (subfolders || []).filter((f) => !f.parent);
  const childFoldersOf = (parentId) => (subfolders || []).filter((f) => f.parent === parentId);

  // Ordered list of all top-level named folders, used for rendering and reorder drag
  const orderedTopItems = React.useMemo(() => {
    const result = [];
    if (folderOrder && folderOrder.length > 0) {
      for (const id of folderOrder) {
        if (id === "general") continue;
        const f = topLevelFolders.find(tf => String(tf.id) === String(id));
        if (f) result.push(f);
      }
    }
    // Append any top-level folders not in folderOrder
    for (const f of topLevelFolders) {
      if (!result.find(r => String(r.id) === String(f.id))) result.push(f);
    }
    return result;
  }, [folderOrder, topLevelFolders]);

  const startRename = (note) => { setEditingTitleId(note.id); setTitleDraft(note.title); };
  const commitRename = (id) => { if (titleDraft.trim()) onRenameNote(id, titleDraft.trim()); setEditingTitleId(null); };
  const startEditContent = (note) => { setEditingContentId(note.id); setContentDraft(note.content); };
  const commitContent = (id) => { onUpdateNote(id, contentDraft); setEditingContentId(null); setShowLinkInput(false); };
  const cancelContent = () => { setEditingContentId(null); setShowLinkInput(false); };

  const wrapText = (before, after) => {
    const ta = contentInputRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = contentDraft.slice(start, end);
    const word = selected || "text";
    const insert = before + word + after;
    const newText = contentDraft.slice(0, start) + insert + contentDraft.slice(end);
    setContentDraft(newText);
    requestAnimationFrame(() => {
      if (contentInputRef.current) {
        contentInputRef.current.selectionStart = start + before.length;
        contentInputRef.current.selectionEnd = start + before.length + word.length;
        contentInputRef.current.focus();
        contentInputRef.current.style.height = "auto";
        contentInputRef.current.style.height = contentInputRef.current.scrollHeight + "px";
      }
    });
  };

  const wrapLine = (prefix) => {
    const ta = contentInputRef.current;
    if (!ta) return;
    const pos = ta.selectionStart;
    const text = contentDraft;
    const lineStart = text.lastIndexOf("\n", pos - 1) + 1;
    const lineEndRaw = text.indexOf("\n", pos);
    const lineEnd = lineEndRaw === -1 ? text.length : lineEndRaw;
    const currentLine = text.slice(lineStart, lineEnd);
    const newText = currentLine.startsWith(prefix)
      ? text.slice(0, lineStart) + currentLine.slice(prefix.length) + text.slice(lineEnd)
      : text.slice(0, lineStart) + prefix + currentLine + text.slice(lineEnd);
    setContentDraft(newText);
    requestAnimationFrame(() => {
      if (contentInputRef.current) {
        contentInputRef.current.focus();
        contentInputRef.current.style.height = "auto";
        contentInputRef.current.style.height = contentInputRef.current.scrollHeight + "px";
      }
    });
  };

  const openLinkInput = () => {
    const ta = contentInputRef.current;
    setSavedSel(ta ? { start: ta.selectionStart, end: ta.selectionEnd } : { start: 0, end: 0 });
    setLinkUrl("");
    setShowLinkInput(true);
  };

  const insertLink = () => {
    const url = linkUrl.trim();
    if (!url) { setShowLinkInput(false); return; }
    const { start, end } = savedSel;
    const selected = contentDraft.slice(start, end) || "link text";
    const md = `[${selected}](${url})`;
    const newText = contentDraft.slice(0, start) + md + contentDraft.slice(end);
    setContentDraft(newText);
    setShowLinkInput(false);
    requestAnimationFrame(() => {
      if (contentInputRef.current) {
        contentInputRef.current.focus();
        contentInputRef.current.style.height = "auto";
        contentInputRef.current.style.height = contentInputRef.current.scrollHeight + "px";
      }
    });
  };
  const handleAddNote = () => {
    if (onAddNote) {
      const firstItem = orderedTopItems[0];
      onAddNote("New Note", "", firstItem?.id || null);
    }
  };
  const handleAddFolder = () => {
    if (newFolderName.trim()) {
      onAddSubfolder(newFolderName.trim());
      setNewFolderName("");
      setAddingFolder(false);
    }
  };
  const toggleFolder = (id) => setCollapsedFolders((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    try { localStorage.setItem("notes-collapsed-folders", JSON.stringify([...next])); } catch {}
    return next;
  });

  // ── Drag helpers ─────────────────────────────────────────
  const clearDrag = () => {
    setDragNoteId(null); setDragFolderId(null);
    setDragOverNoteTarget(undefined); setDragOverFolderTarget(undefined);
    setFolderDropZone(null);
  };
  const handleNoteDragStart = (e, noteId) => {
    setDragNoteId(noteId); setDragFolderId(null);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleFolderDragStart = (e, folderId) => {
    e.stopPropagation();
    setDragFolderId(folderId); setDragNoteId(null);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOverFolderHeader = (e, folderId) => {
    e.preventDefault(); e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    if (dragFolderId !== null) { setDragOverFolderTarget(folderId); setDragOverNoteTarget(undefined); }
    else { setDragOverNoteTarget(folderId); setDragOverFolderTarget(undefined); }
  };
  const handleDragOverGeneral = (e) => {
    e.preventDefault();
    if (dragFolderId !== null) { setDragOverFolderTarget(null); setDragOverNoteTarget(undefined); }
    else { setDragOverNoteTarget(null); setDragOverFolderTarget(undefined); }
  };
  const handleDragLeaveHeader = () => { setDragOverNoteTarget(undefined); setDragOverFolderTarget(undefined); };
  const handleDropOnFolder = (e, folderId) => {
    e.preventDefault(); e.stopPropagation();
    if (dragNoteId !== null) onMoveNote(dragNoteId, folderId);
    else if (dragFolderId !== null && dragFolderId !== folderId) onMoveFolderToParent(dragFolderId, folderId);
    clearDrag();
  };
  const handleDropOnGeneral = (e) => {
    e.preventDefault();
    if (dragNoteId !== null) onMoveNote(dragNoteId, null);
    else if (dragFolderId !== null) onMoveFolderToParent(dragFolderId, null);
    clearDrag();
  };

  // Top-level folder header drag-over: top half = insert before, bottom half = insert after
  const handleTopFolderHeaderDragOver = (e, itemId) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    if (dragFolderId !== null && String(dragFolderId) !== String(itemId)) {
      const rect = e.currentTarget.getBoundingClientRect();
      const pos = e.clientY < rect.top + rect.height / 2 ? "before" : "after";
      setFolderDropZone({ targetId: itemId, pos });
      setDragOverNoteTarget(undefined);
    } else if (dragNoteId !== null) {
      setDragOverNoteTarget(itemId);
      setFolderDropZone(null);
    }
  };

  const handleTopFolderHeaderDrop = (e, itemId) => {
    e.preventDefault();
    e.stopPropagation();
    if (dragFolderId !== null && String(dragFolderId) !== String(itemId)) {
      if (folderDropZone) onReorderFolderOrder?.(dragFolderId, itemId, folderDropZone.pos);
    } else if (dragNoteId !== null) {
      onMoveNote(dragNoteId, itemId === "general" ? null : itemId);
    }
    clearDrag();
  };

  const renderNoteCard = (note) => (
    <div
      key={note.id}
      className="note-card"
      draggable
      onDragStart={(e) => handleNoteDragStart(e, note.id)}
      onDragEnd={clearDrag}
      style={{ opacity: dragNoteId === note.id ? 0.45 : 1, cursor: dragNoteId ? "grabbing" : "default" }}
    >
      <div className="note-card-header">
        <div style={{ flex: 1, minWidth: 0 }}>
          {editingTitleId === note.id ? (
            <input
              ref={titleInputRef}
              className="note-title-input"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={() => commitRename(note.id)}
              onKeyDown={(e) => { if (e.key === "Enter") commitRename(note.id); if (e.key === "Escape") setEditingTitleId(null); }}
            />
          ) : (
            <div className="note-title" title="Click to rename" onClick={() => startRename(note)} style={{ cursor: "text" }}>
              ✏️ {note.title}
            </div>
          )}
          <div className="note-ts">{note.timestamp}</div>
        </div>
        <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
          <button className="note-edit-btn" title="Edit content"
            onClick={() => editingContentId === note.id ? cancelContent() : startEditContent(note)}>
            {editingContentId === note.id ? t("cancel") : (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            )}
          </button>
          <button className="note-delete" onClick={() => askDeleteNote(note)} title="Remove note">×</button>
        </div>
      </div>

      {editingContentId === note.id ? (
        <div className="note-edit-area">
          <div className="rich-toolbar">
            <button className="rich-btn" title="Bold" onMouseDown={(e) => { e.preventDefault(); wrapText("**", "**"); }}><b>B</b></button>
            <button className="rich-btn rich-btn--italic" title="Italic" onMouseDown={(e) => { e.preventDefault(); wrapText("*", "*"); }}><i>I</i></button>
            <button className="rich-btn" title="Strikethrough" onMouseDown={(e) => { e.preventDefault(); wrapText("~~", "~~"); }}><s>S</s></button>
            <button className="rich-btn rich-btn--highlight" title="Highlight" onMouseDown={(e) => { e.preventDefault(); wrapText("==", "=="); }}>H</button>
            <span className="rich-toolbar-sep" />
            <button className="rich-btn" title="Heading 1" onMouseDown={(e) => { e.preventDefault(); wrapLine("# "); }}>H1</button>
            <button className="rich-btn" title="Heading 2" onMouseDown={(e) => { e.preventDefault(); wrapLine("## "); }}>H2</button>
            <button className="rich-btn" title="Bullet list" onMouseDown={(e) => { e.preventDefault(); wrapLine("- "); }}>•</button>
            <span className="rich-toolbar-sep" />
            <button className="rich-btn rich-btn--code" title="Inline code" onMouseDown={(e) => { e.preventDefault(); wrapText("`", "`"); }}>{"</>"}</button>
            <button className="rich-btn" title="Add link" onMouseDown={(e) => { e.preventDefault(); openLinkInput(); }}>🔗</button>
          </div>
          {showLinkInput && (
            <div className="link-input-row">
              <input
                className="link-url-input"
                placeholder="https://..."
                value={linkUrl}
                autoFocus
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") insertLink(); if (e.key === "Escape") setShowLinkInput(false); }}
              />
              <button className="action-btn primary" style={{ padding: "4px 10px", marginTop: 0, fontSize: "0.8rem" }} onMouseDown={(e) => { e.preventDefault(); insertLink(); }}>{t("insertLinkBtn")}</button>
              <button className="action-btn" style={{ padding: "4px 8px", marginTop: 0, fontSize: "0.8rem" }} onMouseDown={(e) => { e.preventDefault(); setShowLinkInput(false); }}>✕</button>
            </div>
          )}
          <textarea
            ref={contentInputRef}
            className="note-content-textarea"
            value={contentDraft}
            onChange={(e) => { setContentDraft(e.target.value); e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }}
            onPaste={(e) => {
              const items = e.clipboardData?.items;
              if (!items) return;
              for (const item of items) {
                if (item.type.startsWith("image/")) {
                  e.preventDefault();
                  const file = item.getAsFile();
                  const formData = new FormData();
                  formData.append("image", file, file.name || "pasted.png");
                  const ta = contentInputRef.current;
                  const start = ta.selectionStart, end = ta.selectionEnd;
                  getAuthHeaders().then((authHeaders) => {
                    fetch("/api/notes/images", { method: "POST", body: formData, headers: authHeaders })
                      .then((r) => r.json())
                      .then((data) => {
                        if (!data.url) return;
                        const md = `![image](${data.url})`;
                        setContentDraft((prev) => prev.slice(0, start) + md + prev.slice(end));
                        requestAnimationFrame(() => {
                          if (contentInputRef.current) {
                            contentInputRef.current.selectionStart = contentInputRef.current.selectionEnd = start + md.length;
                            contentInputRef.current.style.height = "auto";
                            contentInputRef.current.style.height = contentInputRef.current.scrollHeight + "px";
                          }
                        });
                      })
                      .catch((err) => console.error("Image upload failed", err));
                  });
                  break;
                }
              }
            }}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button className="action-btn primary" style={{ flex: 1, padding: "7px" }} onClick={() => commitContent(note.id)}>{t("saveNoteBtn")}</button>
            <button className="action-btn" style={{ flex: 1, padding: "7px" }} onClick={cancelContent}>{t("cancel")}</button>
          </div>
        </div>
      ) : (
        <AiResult content={note.content} />
      )}
    </div>
  );

  const renderFolderSection = (folder, depth = 0) => {
    const folderNotes = notesByFolder[folder.id] || [];
    const children = childFoldersOf(folder.id);
    const isCollapsed = collapsedFolders.has(folder.id);
    const isOver = dragOverNoteTarget === folder.id || dragOverFolderTarget === folder.id;
    const isDragging = dragFolderId === folder.id;
    return (
      <div key={folder.id} className="notes-folder-section" style={{ marginLeft: depth * 14 }}>
        <div
          className={`notes-folder-header${isOver ? " drag-over" : ""}${isDragging ? " folder-dragging" : ""}`}
          draggable
          onDragStart={(e) => handleFolderDragStart(e, folder.id)}
          onDragEnd={clearDrag}
          onDragOver={(e) => handleDragOverFolderHeader(e, folder.id)}
          onDragLeave={handleDragLeaveHeader}
          onDrop={(e) => handleDropOnFolder(e, folder.id)}
        >
          <button className="folder-toggle-btn" onClick={(e) => { e.stopPropagation(); toggleFolder(folder.id); }}>
            {isCollapsed ? "▶" : "▼"}
          </button>
          <span className="folder-name">📁 {folder.name}</span>
          <span className="folder-count">{folderNotes.length}</span>
          <button className="folder-delete-btn" title="Delete folder" onClick={(e) => { e.stopPropagation(); askDeleteFolder(folder); }}>×</button>
        </div>
        {!isCollapsed && (
          <div className="notes-folder-body">
            {children.map((child) => renderFolderSection(child, depth + 1))}
            {folderNotes.length === 0 && children.length === 0
              ? <div className="folder-empty-hint">{t("dropHereFolders")}</div>
              : folderNotes.map(renderNoteCard)}
          </div>
        )}
      </div>
    );
  };

  if (notes.length === 0 && (subfolders || []).length === 0) {
    return (
      <div className="tab-content">
        <div className="no-book-notice">
          <span style={{ fontSize: "3rem" }}>📌</span>
          <p>{t("notesEmptyTitle")}</p>
          <p style={{ fontSize: "0.8rem", marginTop: "6px", color: "#94a3b8" }}>
            {t("notesEmptyHint")}
          </p>
          <div style={{ marginTop: 20, display: "flex", flexDirection: "column", alignItems: "center", gap: 10, width: "100%" }}>
            {!addingFolder ? (
              <button
                className="action-btn primary"
                style={{ width: "auto", padding: "8px 20px", fontSize: "0.85rem" }}
                onClick={() => setAddingFolder(true)}
              >
                {t("addFolderBtn")}
              </button>
            ) : (
              <div className="new-folder-row" style={{ justifyContent: "center" }}>
                <input
                  ref={newFolderRef}
                  className="note-title-input"
                  style={{ flex: 1, maxWidth: 220 }}
                  placeholder={t("folderNamePlaceholder")}
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddFolder();
                    if (e.key === "Escape") { setAddingFolder(false); setNewFolderName(""); }
                  }}
                />
                <button className="action-btn primary" style={{ width: "auto", padding: "6px 14px", marginTop: 0 }} onClick={handleAddFolder}>{t("createBtn")}</button>
                <button className="action-btn" style={{ width: "auto", padding: "6px 10px", marginTop: 0 }} onClick={() => { setAddingFolder(false); setNewFolderName(""); }}>✕</button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="tab-content">
      {/* Header row */}
      <div className="notes-header">
        <span className="notes-count">{lang === "zh" ? `${notes.length} 条笔记` : `${notes.length} note${notes.length !== 1 ? "s" : ""}`}</span>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <button className="action-btn primary" style={{ width: "auto", padding: "6px 12px", marginTop: 0, fontSize: "0.8rem" }} onClick={handleAddNote} title={lang === "zh" ? "新笔记默认进第一个文件夹" : "New note goes into the first folder"}>
            {t("addNoteBtn")}
          </button>
          <button className="action-btn" style={{ width: "auto", padding: "6px 12px", marginTop: 0, fontSize: "0.8rem" }} onClick={() => setAddingFolder(true)}>
            {t("addFolderBtn")}
          </button>

        </div>
      </div>

      {/* New folder input */}
      {addingFolder && (
        <div className="new-folder-row">
          <input
            ref={newFolderRef}
            className="note-title-input"
            style={{ flex: 1, width: "auto" }}
            placeholder={t("folderNamePlaceholder")}
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddFolder();
              if (e.key === "Escape") { setAddingFolder(false); setNewFolderName(""); }
            }}
          />
          <button className="action-btn primary" style={{ width: "auto", padding: "6px 14px", marginTop: 0 }} onClick={handleAddFolder}>{t("createBtn")}</button>
          <button className="action-btn" style={{ width: "auto", padding: "6px 10px", marginTop: 0 }} onClick={() => { setAddingFolder(false); setNewFolderName(""); }}>✕</button>
        </div>
      )}

      {/* All top-level folders — unified ordered list */}
      {orderedTopItems.map((item, idx) => {
        const isBeingDragged = dragFolderId !== null && String(dragFolderId) === String(item.id);
        const reorderPos = folderDropZone && String(folderDropZone.targetId) === String(item.id) ? folderDropZone.pos : null;
        const reorderStyle = {
          opacity: isBeingDragged ? 0.45 : 1,
          boxShadow: reorderPos === "before"
            ? "inset 0 2px 0 0 var(--primary)"
            : reorderPos === "after"
              ? "inset 0 -2px 0 0 var(--primary)"
              : undefined,
        };

        const folderNotes = notesByFolder[item.id] || [];
        const children = childFoldersOf(item.id);
        const isCollapsed = collapsedFolders.has(item.id);
        const noteIsOver = dragOverNoteTarget === item.id;
        return (
          <div key={item.id} className={`notes-folder-section${isBeingDragged ? " folder-dragging" : ""}`} style={reorderStyle}>
            <div
              className={`notes-folder-header${noteIsOver ? " drag-over" : ""}`}
              draggable
              onDragStart={(e) => handleFolderDragStart(e, item.id)}
              onDragEnd={clearDrag}
              onDragOver={(e) => handleTopFolderHeaderDragOver(e, item.id)}
              onDragLeave={() => { setDragOverNoteTarget(undefined); setFolderDropZone(null); }}
              onDrop={(e) => handleTopFolderHeaderDrop(e, item.id)}
            >
              <button className="folder-toggle-btn" onClick={(e) => { e.stopPropagation(); toggleFolder(item.id); }}>
                {isCollapsed ? "▶" : "▼"}
              </button>
              <span className="folder-name">📁 {item.name}</span>
              <span className="folder-count">{folderNotes.length}</span>
              <div className="folder-actions">
                <button className="folder-icon-btn" title={lang === "zh" ? "上移" : "Move up"} disabled={idx === 0} onClick={(e) => { e.stopPropagation(); onReorderFolderOrder?.(item.id, orderedTopItems[idx - 1].id, "before"); }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
                </button>
                <button className="folder-icon-btn" title={lang === "zh" ? "下移" : "Move down"} disabled={idx === orderedTopItems.length - 1} onClick={(e) => { e.stopPropagation(); onReorderFolderOrder?.(item.id, orderedTopItems[idx + 1].id, "after"); }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                </button>
                <button className="folder-icon-btn" title={getFolderSort(item.id) === "newest" ? t("newestFirst") : t("oldestFirst")} onClick={(e) => { e.stopPropagation(); setFolderSort(item.id, getFolderSort(item.id) === "newest" ? "oldest" : "newest"); }}>
                  {getFolderSort(item.id) === "newest"
                    ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="9" y2="18"/></svg>
                    : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="9" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>}
                </button>
                <button className="folder-icon-btn" title={t("exportWord")} onClick={(e) => { e.stopPropagation(); downloadAsWord(folderNotes); }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                </button>
                <button className="folder-icon-btn folder-icon-btn--danger" title="Delete folder" onClick={(e) => { e.stopPropagation(); askDeleteFolder(item); }}>×</button>
              </div>
            </div>
            {!isCollapsed && (
              <div className="notes-folder-body">
                {children.map((child) => renderFolderSection(child, 1))}
                {folderNotes.length === 0 && children.length === 0
                  ? <div className="folder-empty-hint">{t("dropHereFolders")}</div>
                  : folderNotes.map(renderNoteCard)}
              </div>
            )}
          </div>
        );
      })}
    </div>

    {confirmPending && (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
        onClick={() => setConfirmPending(null)}>
        <div style={{ background: "var(--card-bg, #fff)", borderRadius: 12, padding: "24px 28px", minWidth: 280, maxWidth: 340, boxShadow: "0 8px 32px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", gap: 16 }}
          onClick={(e) => e.stopPropagation()}>
          <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>
            {confirmPending.type === "note"
              ? (lang === "zh" ? "删除笔记？" : "Delete note?")
              : (lang === "zh" ? "删除文件夹？" : "Delete folder?")}
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748b", wordBreak: "break-all" }}>
            {confirmPending.type === "note"
              ? (lang === "zh" ? `"${confirmPending.name}" 将被永久删除，无法撤销。` : `"${confirmPending.name}" will be permanently deleted.`)
              : (lang === "zh" ? `"${confirmPending.name}" 及其所有笔记将被永久删除，无法撤销。` : `"${confirmPending.name}" and all its notes will be permanently deleted.`)}
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button className="action-btn" style={{ padding: "6px 16px", fontSize: "0.85rem" }} onClick={() => setConfirmPending(null)}>
              {lang === "zh" ? "取消" : "Cancel"}
            </button>
            <button className="action-btn" style={{ padding: "6px 16px", fontSize: "0.85rem", background: "#ef4444", color: "#fff", border: "none" }} onClick={confirmDelete}>
              {lang === "zh" ? "删除" : "Delete"}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
